import { Notify } from 'quasar'
import ReportService from 'src/services/reports'
import { useUsersStore, useFormsStore } from 'src/stores/index.js'
import { i18n } from 'src/boot/i18n'
import { readBlobErrorCode } from '@/utils/reportSizing'

const REPORT_TOO_LARGE_FOR_DIRECT = 'REPORT_TOO_LARGE_FOR_DIRECT'

// Si la red falla, la vista pasa sola al dialogo de segundo plano: el aviso de
// conexion del interceptor solo estorbaria. El timeout es mucho menor que el de
// los demas reportes (300 s) para no dejar al usuario esperando antes de caer a
// segundo plano cuando el edge no corta la peticion.
const DIRECT_PDF_REQUEST_CONFIG = { suppressConnectionToast: true, timeout: 90000 }

// Sin esto, cualquier rechazo del backend (403 de un reporte acotado, 5xx, timeout) dejaba
// `loading` en true y el spinner girando para siempre. El status puede venir en
// err.response (axios) o en la raiz (responser.js).
function notifyReportError (err) {
  const status = err?.response?.status ?? err?.status
  Notify.create({
    type: 'negative',
    message: i18n.global.t(status === 403 ? 'reports.errors.forbidden' : 'reports.errors.failed')
  })
}

export default {
  updateReportType (reportType) {
    this.reportQuery.reportType = reportType
    this.reportQuery.creator_users = []
    this.reportQuery.reportTypeChild = 'general'
    this.reportQuery.periodicity = 'range'
    this.reportQuery.status = null
    this.result = null
  },
  updateReportStatus (status) {
    this.reportQuery.status = status
  },
  updateReportTypeChild (reportTypeChild) {
    this.reportQuery.reportTypeChild = reportTypeChild
    this.reportQuery.creator_users = []
    this.result = null
  },
  updateReportNoLocation (filter) {
    this.reportQuery.noLocation = filter
  },
  updateReportbyUser (filter) {
    this.reportQuery.byUser = filter
  },
  updateReportCategory (category) {
    this.reportQuery.category = category
  },
  updateReportCategory2 (category) {
    this.reportQuery.category2 = category
  },
  updateTaskGroupBy (filter) {
    this.reportQuery.tasksGroupBy = filter
  },
  updateFormsFilter (filter) {
    this.reportQuery.formsFilter = filter
  },
  updateEventsFilter (filter) {
    this.reportQuery.eventsFilter = filter
  },
  updateActionPlansFilter (filter) {
    this.reportQuery.actionPlansFilter = filter
  },
  updateReportLocations (locations) {
    this.reportQuery.locations = locations
    this.reportQuery.periodicity = 'range'
  },
  updateReportUsers (users) {
    this.reportQuery.users = users
  },
  updateReportCreatorUsers (users) {
    this.reportQuery.creator_users = users
  },
  selectAllUsers () {
    const usersStore = useUsersStore()
    this.reportQuery.users = usersStore.users.map(user => user.id)
  },
  selectAllCreatorUsers () {
    const usersStore = useUsersStore()
    this.reportQuery.creator_users = usersStore.creator_users.map(user => user.id)
  },
  updateReportForms (forms) {
    this.reportQuery.forms = forms
    this.reportQuery.block = null
    this.reportQuery.periodicity = 'range'
  },
  updateReportFormCategory (formCategory) {
    this.reportQuery.formCategory = formCategory
  },
  updateReportGroup (group) {
    this.reportQuery.group = group
    this.reportQuery.block = null
    this.reportQuery.periodicity = 'range'
  },
  updateReportItem (item) {
    this.reportQuery.item = item
    this.reportQuery.block = null
    this.reportQuery.periodicity = 'range'
  },
  updateReportStartDate (startDate) {
    this.reportQuery.startDate = startDate
  },
  updateReportEndDate (endDate) {
    this.reportQuery.endDate = endDate
  },
  updateReportPeriodicity (periodicity) {
    this.reportQuery.periodicity = periodicity
    this.result = null
  },
  updateReportRole (role) {
    this.reportQuery.role = role
  },
  updateReportRoles (roles) {
    this.reportQuery.roles = roles
  },
  updateReportSprint (sprint) {
    this.reportQuery.sprint = sprint
  },
  updateReportGroups (group) {
    this.reportQuery.groups = group
  },
  updateReportBlock (block_id) {
    const formsStore = useFormsStore()
    const block = formsStore.blocks.find(b => b.id === block_id)
    block.forms.forEach(form => {
      if (!formsStore.forms.some(f => f.id === form.id)) {
        formsStore.forms.push({ ...form, name_label: form.name_label || form.name })
      }
    })
    this.reportQuery.forms = block.forms.map(form => form.id)
    this.reportQuery.block = block_id
  },
  updateReportCampaign (campaign) {
    this.reportQuery.campaign = campaign
  },
  updateReportFormat (format) {
    this.reportQuery.format = format
  },
  updateReportFranchiseId (franchiseId) {
    this.reportQuery.franchise_id = franchiseId
  },
  updateReporFolders (folders) {
    this.reportQuery.folders = folders
  },
  updateReportSubFolders (subFolders) {
    this.reportQuery.subFolders = subFolders
  },
  updateReportHierarchyFilters (filters) {
    const isDirectMode = Array.isArray(filters.category)
    if (isDirectMode) {
      this.reportQuery.category = filters.category
      this.reportQuery.category2 = filters.category2
      this.reportQuery.area_id = null
      this.reportQuery.hierarchy_site_id = null
      this.reportQuery.hierarchy_category = null
      this.reportQuery.hierarchy_subcategory_id = null
    } else {
      this.reportQuery.area_id = filters.area_id
      this.reportQuery.hierarchy_site_id = filters.hierarchy_site_id
      this.reportQuery.hierarchy_category = filters.category
      this.reportQuery.hierarchy_subcategory_id = filters.hierarchy_subcategory_id
      this.reportQuery.category = []
      this.reportQuery.category2 = []
    }
  },
  sendReportByEmail (emailList) {
    const query = this.reportQuery
    const payload = {
      ...query,
      forms: query.reportType !== 'photos' ? query.forms.join(',') : query.forms,
      locations: query.locations.join(','),
      users: [...query.users, ...query.creator_users].join(','),
      emails: emailList.join(',')
    }

    payload.format = 'pdf'

    const promise = ReportService.sendReportByEmail(payload)

    promise.catch(err => console.error(err))

    return promise
  },
  sendGeneralReportPdfByEmail (emailList) {
    const query = this.reportQuery
    const payload = {
      ...query,
      forms: query.forms.join(','),
      locations: query.locations.join(',')
    }

    return ReportService.sendGeneralReportPdfByEmail(emailList, payload)
  },
  requestPhotosReportByNotification () {
    const query = this.reportQuery
    const payload = {
      ...query,
      forms: Array.isArray(query.forms) ? query.forms.join(',') : query.forms,
      locations: query.locations.join(',')
    }

    return ReportService.requestPhotosReportByNotification(payload)
  },
  sendPhotosReportByEmail (emailList) {
    const query = this.reportQuery
    const payload = {
      ...query,
      forms: Array.isArray(query.forms) ? query.forms.join(',') : query.forms,
      locations: query.locations.join(',')
    }

    return ReportService.sendPhotosReportByEmail(emailList, payload)
  },
  printReport (payload) {
    this.printingReport = payload
  },
  downloadReportPDF (reportName) {
    this.loading = true
    const reportQuery = this.reportQuery
    const query = {
      ...reportQuery,
      forms: reportQuery.reportType !== 'photos' ? reportQuery.forms.join(',') : reportQuery.forms,
      locations: reportQuery.locations.join(','),
      users: [...reportQuery.users, ...reportQuery.creator_users].join(',')
    }

    query.format = 'pdf'

    const payload = {
      query,
      reportName
    }
    ReportService.getReport(payload)
      .catch(notifyReportError)
      .finally(() => this.loading = false)
  },
  // Descarga directa del PDF del Reporte General. No toca `loading`: desmonta la
  // pantalla de resultados y con ella el dialogo al que la vista puede pasar si
  // el PDF resulta muy grande ('too-large') o la peticion se queda sin respuesta
  // ('timeout'). Cualquier otro error se propaga.
  async downloadGeneralReportPdfDirect (reportName) {
    const reportQuery = this.reportQuery
    const query = {
      ...reportQuery,
      forms: reportQuery.forms.join(','),
      locations: reportQuery.locations.join(','),
      users: [...reportQuery.users, ...reportQuery.creator_users].join(','),
      format: 'pdf'
    }

    try {
      await ReportService.getReport({ query, reportName, requestConfig: DIRECT_PDF_REQUEST_CONFIG })

      return { status: 'downloaded' }
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 422 && await readBlobErrorCode(error) === REPORT_TOO_LARGE_FOR_DIRECT) {
        return { status: 'too-large' }
      }
      // Sin respuesta tambien llega el 504 del edge cuando no trae cabeceras CORS.
      if (status === 504 || (error?.isAxiosError && !error.response)) {
        return { status: 'timeout' }
      }

      throw error
    }
  },
  updateReport (reportName) {
    this.loading = true
    const usersStore = useUsersStore()
    const reportQuery = this.reportQuery

    const query = {
      ...reportQuery,
      forms: (reportQuery.reportType !== 'daily' && reportQuery.reportType !== 'photos')
        ? reportQuery.forms.join(',')
        : reportQuery.forms,
      locations: reportQuery.locations.join(','),
      users: [...reportQuery.users, ...reportQuery.creator_users].join(',')
    }

    if (query.reportType === 'user' && query.users.length === 0) {
      query.users = usersStore.users.map(user => user.id).join(',')
    }
    if (query.reportType === 'auditor_role' && query.roles.length > 0) {
      query.roles = reportQuery.roles.join(',')
    }
    if (reportQuery.reportType === 'tasks') {
      query.users = [...reportQuery.users].join(',')
      query.users_creator = [...reportQuery.creator_users].join(',')

      if (reportQuery.category) query.category = [...reportQuery.category].join(',')
      if (reportQuery.category2) query.category2 = [...reportQuery.category2].join(',')
    }
    if (reportQuery.reportType === 'photos') query.newReport = true
    if (reportQuery.reportType === 'fullfillment') {
      query.formCategory = reportQuery.formCategory.join(',')
    }

    const payload = {
      query,
      reportName
    }

    ReportService.getReport(payload).then(res => {
      if (payload.query.format !== 'json') {
        this.loading = false
      } else {
        this.expandedFilters = false
        this.result = res.data
        this.loading = false
        this.oldReportQuery = { ...this.reportQuery }
      }
    }).catch(err => {
      this.loading = false
      notifyReportError(err)
    })
  },
  deselectUsers () {
    this.reportQuery.users = []
  },
  deselectCreatorUsers () {
    this.reportQuery.creator_users = []
  }
}
